import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Send, MessageCircle } from 'lucide-react';

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-100px" },
  transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] }
};

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const subject = `New Inquiry from ${formData.name}${formData.company ? ` - ${formData.company}` : ''}`;
    const body = `Name: ${formData.name}
Email: ${formData.email}
Company: ${formData.company || 'Not provided'}

Message:
${formData.message}`;

    const mailtoLink = `mailto:zenithmarketingindia@gmail.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoLink;
  };

  const handleWhatsApp = () => {
    const message = `Hi, I'm interested in your marketing services. My name is ${formData.name || '[Your Name]'}${formData.company ? ` from ${formData.company}` : ''}.`;
    window.open(`https://wa.me/919928279319?text=${encodeURIComponent(message)}`, '_blank');
  };

  return (
    <div className="w-full pt-20">
      {/* Hero Section */}
      <section className="py-24 md:py-32 relative overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900" data-testid="contact-hero">
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-bl from-brand-blue/20 via-cyan-500/10 to-transparent rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-gradient-to-tr from-purple-500/10 to-transparent rounded-full blur-3xl" />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center max-w-3xl mx-auto">
            <span className="text-xs md:text-sm font-bold uppercase tracking-[0.2em] text-brand-blue mb-4 block">CONTACT</span>
            <h1 className="font-outfit text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.1] text-white mb-6 uppercase">
              LET'S TALK ABOUT YOUR
              <span className="bg-gradient-to-r from-brand-blue to-cyan-400 bg-clip-text text-transparent"> NEXT BIG MOVE</span>
            </h1>
            <p className="text-lg md:text-xl text-white/70 leading-relaxed">
              Ready to take your brand to the next level? Get in touch with us today.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
            {/* Contact Information */}
            <motion.div {...fadeInUp}>
              <h2 className="font-outfit text-3xl font-bold text-gray-900 mb-6 uppercase">GET IN TOUCH</h2>
              <p className="text-lg text-gray-600 mb-12 leading-relaxed">
                Whether you're looking to scale your business, launch a new brand, or enhance your digital presence, we're here to help. Reach out and let's create something extraordinary together.
              </p>

              <div className="space-y-8">
                {/* Address */}
                <div className="flex items-start" data-testid="contact-address">
                  <div className="w-14 h-14 bg-gradient-to-br from-brand-blue/10 to-cyan-400/10 rounded-2xl flex items-center justify-center flex-shrink-0 mr-5">
                    <MapPin className="text-brand-blue w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-outfit text-xl font-semibold text-gray-900 mb-2 uppercase">OFFICE ADDRESS</h3>
                    <p className="text-gray-600 leading-relaxed">
                      D-10, S2, Airport Enclave<br />
                      Jagatpura, Jaipur<br />
                      Rajasthan, India
                    </p>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex items-start" data-testid="contact-phone">
                  <div className="w-14 h-14 bg-gradient-to-br from-brand-blue/10 to-cyan-400/10 rounded-2xl flex items-center justify-center flex-shrink-0 mr-5">
                    <Phone className="text-brand-blue w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-outfit text-xl font-semibold text-gray-900 mb-2 uppercase">PHONE</h3>
                    <div className="flex flex-wrap gap-3 mb-2">
                      <a 
                        href="tel:+919928279319" 
                        data-testid="call-link"
                        className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-brand-blue to-cyan-400 text-white rounded-full text-sm font-medium hover:from-cyan-400 hover:to-brand-blue transition-all duration-300 uppercase"
                      >
                        <Phone size={16} className="mr-2" />
                        CALL NOW
                      </a>
                      <a 
                        href="https://wa.me/919928279319?text=Hi%2C%20I%27m%20interested%20in%20your%20marketing%20services."
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="whatsapp-link"
                        className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-full text-sm font-medium hover:from-green-600 hover:to-green-500 transition-all duration-300 uppercase"
                      >
                        <MessageCircle size={16} className="mr-2" />
                        WHATSAPP
                      </a>
                    </div>
                    <p className="text-gray-600">+91 9928279319</p>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-start" data-testid="contact-email">
                  <div className="w-14 h-14 bg-gradient-to-br from-brand-blue/10 to-cyan-400/10 rounded-2xl flex items-center justify-center flex-shrink-0 mr-5">
                    <Mail className="text-brand-blue w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-outfit text-xl font-semibold text-gray-900 mb-2 uppercase">EMAIL</h3>
                    <a href="mailto:info@zenithmarketingindia.com" className="text-gray-600 hover:text-brand-blue transition-colors block">
                      info@zenithmarketingindia.com
                    </a>
                    <a href="mailto:zenithmarketingindia@gmail.com" className="text-gray-600 hover:text-brand-blue transition-colors block">
                      zenithmarketingindia@gmail.com
                    </a>
                  </div>
                </div>
              </div>

              {/* Business Hours */}
              <div className="mt-12 p-6 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl border border-gray-200">
                <h3 className="font-outfit text-xl font-semibold text-gray-900 mb-4 uppercase">BUSINESS HOURS</h3>
                <div className="space-y-2 text-gray-600">
                  <p><span className="font-medium text-gray-900">Monday - Friday:</span> 9:00 AM - 6:00 PM</p>
                  <p><span className="font-medium text-gray-900">Saturday:</span> 10:00 AM - 4:00 PM</p>
                  <p><span className="font-medium text-gray-900">Sunday:</span> Closed</p>
                </div>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div 
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className="bg-white p-8 md:p-10 rounded-3xl border border-gray-200 shadow-xl">
                <h2 className="font-outfit text-2xl font-bold text-gray-900 mb-6 uppercase">SEND US A MESSAGE</h2>
                
                <form onSubmit={handleSubmit} data-testid="contact-form">
                  <div className="space-y-6">
                    {/* Name */}
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-900 mb-2 uppercase">
                        NAME *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        data-testid="contact-name-input"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue transition-all outline-none"
                        placeholder="Your name"
                      />
                    </div>

                    {/* Email */}
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-900 mb-2 uppercase">
                        EMAIL *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        data-testid="contact-email-input"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue transition-all outline-none"
                        placeholder="your@email.com"
                      />
                    </div>

                    {/* Company */}
                    <div>
                      <label htmlFor="company" className="block text-sm font-medium text-gray-900 mb-2 uppercase">
                        COMPANY
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        data-testid="contact-company-input"
                        value={formData.company}
                        onChange={handleChange}
                        className="w-full px-4 py-3.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue transition-all outline-none"
                        placeholder="Your company name"
                      />
                    </div>

                    {/* Message */}
                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-gray-900 mb-2 uppercase">
                        MESSAGE *
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        data-testid="contact-message-input"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={5}
                        className="w-full px-4 py-3.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue transition-all outline-none resize-none"
                        placeholder="Tell us about your project..."
                      />
                    </div>

                    {/* Submit Buttons */}
                    <div className="flex flex-col sm:flex-row gap-4 pt-2">
                      <button
                        type="submit"
                        data-testid="contact-submit-button"
                        className="flex-1 inline-flex items-center justify-center px-8 py-4 rounded-full bg-gradient-to-r from-brand-blue to-cyan-400 text-white font-medium transition-all duration-300 hover:from-cyan-400 hover:to-brand-blue hover:shadow-lg shadow-brand-blue/30 uppercase"
                      >
                        SEND EMAIL
                        <Send className="ml-2 w-5 h-5" />
                      </button>
                      <button
                        type="button"
                        onClick={handleWhatsApp}
                        data-testid="contact-whatsapp-button"
                        className="flex-1 inline-flex items-center justify-center px-8 py-4 rounded-full bg-gradient-to-r from-green-500 to-green-600 text-white font-medium transition-all duration-300 hover:from-green-600 hover:to-green-500 hover:shadow-lg uppercase"
                      >
                        WHATSAPP
                        <MessageCircle className="ml-2 w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-gradient-to-br from-brand-blue via-cyan-500 to-brand-blue relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-0 left-0 w-[400px] h-[400px] bg-white/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-[300px] h-[300px] bg-white/10 rounded-full blur-3xl" />
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeInUp}>
            <h2 className="font-outfit text-4xl md:text-5xl font-bold text-white mb-6 uppercase">
              LET'S CREATE SOMETHING AMAZING
            </h2>
            <p className="text-white/80 text-lg md:text-xl mb-4">
              We're excited to learn about your brand and explore how we can help you achieve your goals.
            </p>
            <p className="text-white/60 text-lg">
              Follow us on social media to stay updated with our latest work and insights.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
